import type { CSSProperties } from 'react'
import { ShoppingBag } from 'lucide-react'

/**
 * A stand-in storefront for tenants who have not connected their site yet.
 *
 * It is a sketch, not the real page: a sample menu drawn with the tenant's
 * name, logo, colours and photos, following the design choices closely enough
 * that every control in the editor visibly does something. Once an address is
 * connected the live frame replaces it.
 */
export interface MockDesign {
  name: string
  logo: string
  brand: string
  accent: string
  /** The page colour for the mode being previewed. */
  canvas: string
  fontHeading: string
  fontBody: string
  upper: boolean
  italic: boolean
  radius: string
  buttonShape: string
  cardStyle: string
  navStyle: string
  menuLayout: string
  heroVariant: string
  headline: string
  subcopy: string
  photos: string[]
}

const ITEMS = [
  { name: 'Chicken tikka roll', note: 'Charcoal chicken, mint chutney', price: 'Rs 650', deal: true },
  { name: 'Smash burger', note: 'Double patty, house sauce', price: 'Rs 890', deal: false },
  { name: 'Loaded fries', note: 'Cheese, jalapeño, garlic mayo', price: 'Rs 520', deal: false },
  { name: 'Karak chai', note: 'Slow-brewed, cardamom', price: 'Rs 250', deal: false },
]
const CATEGORIES = ['Popular', 'Grills', 'Burgers', 'Drinks']

function rgb(hex: string): [number, number, number] {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  const n = m ? parseInt(m[1], 16) : 0xffffff
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}
function luma(hex: string): number {
  const [r, g, b] = rgb(hex)
  return (r * 299 + g * 587 + b * 114) / 1000
}
function mix(a: string, b: string, t: number): string {
  const x = rgb(a)
  const y = rgb(b)
  return `#${x.map((c, i) => Math.round(c + (y[i] - c) * t).toString(16).padStart(2, '0')).join('')}`
}
const on = (hex: string) => (luma(hex) > 150 ? '#171613' : '#ffffff')

export function MockStorefront({ d }: { d: MockDesign }) {
  const dark = luma(d.canvas) < 110
  const ink = dark ? '#F4F0E9' : '#171613'
  const muted = mix(d.canvas, ink, 0.6)
  const line = mix(d.canvas, ink, 0.14)
  const tint = mix(d.canvas, ink, 0.07)
  const surface = dark ? mix(d.canvas, '#ffffff', 0.06) : mix(d.canvas, '#ffffff', 0.85)
  const r = d.radius === 'sharp' ? 3 : d.radius === 'pill' ? 22 : 12
  const btnR = d.buttonShape === 'pill' ? 999 : d.buttonShape === 'rounded' ? 8 : 2
  const brandFg = on(d.brand)

  const heading: CSSProperties = {
    fontFamily: `'${d.fontHeading}', serif`,
    textTransform: d.upper ? 'uppercase' : 'none',
    fontStyle: d.italic ? 'italic' : 'normal',
  }
  const card: CSSProperties =
    d.cardStyle === 'raised'
      ? { background: surface, borderRadius: r, boxShadow: '0 1px 2px rgba(0,0,0,.08), 0 6px 16px rgba(0,0,0,.06)' }
      : d.cardStyle === 'outline'
        ? { border: `1px solid ${line}`, borderRadius: r }
        : d.cardStyle === 'flat'
          ? { background: tint, borderRadius: r }
          : d.cardStyle === 'sticker'
            ? { background: surface, border: `2px solid ${ink}`, boxShadow: `3px 3px 0 ${ink}`, borderRadius: r }
            : { borderBottom: `1px solid ${line}` }
  const bare = d.cardStyle === 'bare'

  const photo = (i: number): CSSProperties =>
    d.photos.length > 0
      ? { backgroundImage: `url("${d.photos[i % d.photos.length]}")`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: mix(d.canvas, d.brand, 0.14 + (i % 3) * 0.07) }

  const add = (
    <span className="shrink-0 px-3 py-1.5 text-[11px] font-semibold leading-none" style={{ background: d.brand, color: brandFg, borderRadius: btnR }}>
      Add
    </span>
  )
  const badge = (
    <span className="px-1.5 py-0.5 text-[10px] font-bold leading-none" style={{ background: d.accent, color: on(d.accent), borderRadius: Math.min(r, 6) }}>
      20% off
    </span>
  )

  const typeHero = ['type', 'minimal', 'receipt'].includes(d.heroVariant)
  const brandHero = ['billboard', 'split', 'poster', 'collage', 'lineup'].includes(d.heroVariant) || d.photos.length === 0
  const twoUp = ['tiles', 'magazine', 'gallery'].includes(d.menuLayout)
  const board = d.menuLayout === 'board'
  const shelf = d.menuLayout === 'shelf'

  return (
    <div className="flex h-full w-full flex-col overflow-hidden text-left" style={{ background: d.canvas, color: ink, fontFamily: `'${d.fontBody}', sans-serif` }}>
      <div className="flex shrink-0 items-center gap-2.5 px-4 py-3" style={{ borderBottom: `1px solid ${line}` }}>
        {d.logo ? (
          <img src={d.logo} alt="" className="h-8 w-8 object-cover" style={{ borderRadius: Math.min(r, 10) }} />
        ) : (
          <span className="flex h-8 w-8 items-center justify-center text-sm font-bold" style={{ background: d.brand, color: brandFg, borderRadius: Math.min(r, 10) }}>
            {d.name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="min-w-0 flex-1 truncate text-sm font-semibold">{d.name}</span>
        <span className="flex h-8 w-8 items-center justify-center" style={{ background: tint, borderRadius: btnR }}>
          <ShoppingBag className="h-4 w-4" />
        </span>
      </div>

      {typeHero ? (
        <div className={d.heroVariant === 'minimal' ? 'shrink-0 px-4 pb-2 pt-5' : 'shrink-0 px-4 pb-4 pt-9 text-center'}>
          <div className="text-[2.6rem] leading-[0.95]" style={heading}>
            {d.name}
          </div>
          {d.headline && <p className="mt-2 text-sm" style={{ color: muted }}>{d.headline}</p>}
        </div>
      ) : brandHero ? (
        <div className="relative shrink-0 px-4 pb-6 pt-7" style={{ background: d.brand, color: brandFg }}>
          <div className="max-w-[70%] text-[2.2rem] leading-[0.95]" style={heading}>
            {d.name}
          </div>
          {d.headline && <p className="mt-2 max-w-[68%] text-xs opacity-90">{d.headline}</p>}
          <div
            className="absolute -bottom-5 right-4 h-24 w-24"
            style={{
              ...photo(0),
              borderRadius: d.heroVariant === 'billboard' ? 999 : r,
              border: `3px solid ${d.canvas}`,
              ...(d.photos.length === 0 ? { background: mix(d.brand, '#ffffff', 0.25) } : null),
            }}
          />
        </div>
      ) : (
        <div className="relative h-52 shrink-0" style={photo(0)}>
          <div className="absolute inset-0 flex flex-col justify-end p-4" style={{ background: 'linear-gradient(to top, rgba(0,0,0,.72), rgba(0,0,0,.05))', color: '#fff' }}>
            <div className="text-[2.2rem] leading-[0.95]" style={heading}>
              {d.name}
            </div>
            {d.headline && <p className="mt-1.5 text-xs opacity-90">{d.headline}</p>}
          </div>
        </div>
      )}

      <div className="min-h-0 flex-1 px-4 pt-4">
        {d.subcopy && <p className="mb-3 text-xs" style={{ color: muted }}>{d.subcopy}</p>}
        <div className="flex gap-1.5 text-[11px] font-semibold">
          {['Pickup', 'Delivery'].map((t, i) => (
            <span key={t} className="px-2.5 py-1" style={{ borderRadius: btnR, ...(i === 0 ? { background: ink, color: d.canvas } : { border: `1px solid ${line}` }) }}>
              {t}
            </span>
          ))}
        </div>

        <div className="mt-4 flex gap-2 overflow-hidden text-xs font-medium" style={d.navStyle === 'pills' ? undefined : { borderBottom: `1px solid ${line}` }}>
          {CATEGORIES.map((c, i) =>
            d.navStyle === 'pills' ? (
              <span key={c} className="shrink-0 px-3 py-1.5" style={{ borderRadius: btnR, ...(i === 0 ? { background: d.brand, color: brandFg } : { background: tint }) }}>
                {c}
              </span>
            ) : (
              <span key={c} className="shrink-0 px-1 pb-2" style={i === 0 ? { borderBottom: `2px solid ${d.brand}`, marginBottom: -1 } : { color: muted }}>
                {c}
              </span>
            ),
          )}
        </div>

        <div className="mb-3 mt-5 text-2xl leading-none" style={heading}>
          Popular
        </div>

        {board ? (
          <div className="space-y-3">
            {ITEMS.map((it) => (
              <div key={it.name}>
                <div className="flex items-baseline gap-2 text-sm font-semibold">
                  <span>{it.name}</span>
                  <span className="flex-1 -translate-y-1" style={{ borderBottom: `1px dotted ${muted}` }} />
                  <span>{it.price}</span>
                </div>
                <div className="text-[11px]" style={{ color: muted }}>{it.note}</div>
              </div>
            ))}
          </div>
        ) : shelf ? (
          <div className="flex gap-3 overflow-hidden">
            {ITEMS.map((it, i) => (
              <div key={it.name} className="w-36 shrink-0 overflow-hidden" style={bare ? undefined : card}>
                <div className="h-28" style={{ ...photo(i), borderRadius: bare ? r : 0 }} />
                <div className="p-2.5">
                  <div className="truncate text-xs font-semibold">{it.name}</div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold">{it.price}</span>
                    {add}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : twoUp ? (
          <div className="grid grid-cols-2 gap-3">
            {ITEMS.map((it, i) => (
              <div key={it.name} className="overflow-hidden" style={bare ? undefined : card}>
                <div className={d.menuLayout === 'gallery' && i % 2 === 0 ? 'h-36' : 'h-28'} style={{ ...photo(i), borderRadius: bare ? r : 0 }} />
                <div className={bare ? 'pt-2' : 'p-2.5'}>
                  <div className="truncate text-xs font-semibold">{it.name}</div>
                  <div className="mt-1.5 flex items-center justify-between gap-2">
                    <span className="text-xs font-bold">{it.price}</span>
                    {it.deal ? badge : add}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={bare ? 'space-y-0' : 'space-y-2.5'}>
            {ITEMS.map((it, i) => (
              <div key={it.name} className={bare ? 'flex items-center gap-3 py-3' : 'flex items-center gap-3 p-2.5'} style={card}>
                {d.menuLayout !== 'list' && (
                  <div className={d.menuLayout === 'rows' ? 'h-20 w-24 shrink-0' : 'h-16 w-16 shrink-0'} style={{ ...photo(i), borderRadius: Math.max(2, r - 4) }} />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5">
                    <span className="truncate text-sm font-semibold">{it.name}</span>
                    {it.deal && badge}
                  </div>
                  <div className="truncate text-[11px]" style={{ color: muted }}>{it.note}</div>
                  <div className="mt-1 text-xs font-bold">{it.price}</div>
                </div>
                {add}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
